import { Link } from "react-router-dom";
import { Phone, Printer, MapPin, Clock, Facebook, Twitter } from "lucide-react";
import { COMPANY, LISTINGS } from "@/lib/constants";
import { AREAS } from "@/data/areas";

export const Footer = () => (
  <footer data-testid="site-footer" className="bg-primary text-primary-foreground mt-24">
    <div className="container-page py-16 grid gap-10 md:grid-cols-2 lg:grid-cols-5">
      <div>
        <div className="inline-flex items-center bg-white rounded-2xl px-4 py-3 mb-4">
          <img src="/esprit-logo.png" alt="Esprit Home Healthcare" className="h-11 w-auto" />
        </div>
        <p className="text-primary-foreground/80 leading-relaxed">
          {COMPANY.tagline} Skilled nursing, therapy, and personal care delivered across Northern Virginia.
        </p>
        <div className="flex gap-3 mt-5">
          <a href="https://facebook.com" target="_blank" rel="noreferrer" aria-label="Facebook" className="grid place-items-center w-11 h-11 rounded-full bg-primary-foreground/10 hover:bg-primary-foreground/20 transition-colors">
            <Facebook className="w-5 h-5" />
          </a>
          <a href="https://twitter.com" target="_blank" rel="noreferrer" aria-label="Twitter" className="grid place-items-center w-11 h-11 rounded-full bg-primary-foreground/10 hover:bg-primary-foreground/20 transition-colors">
            <Twitter className="w-5 h-5" />
          </a>
          <a href={COMPANY.gbp} target="_blank" rel="noreferrer" aria-label="Google Business Profile" className="grid place-items-center w-11 h-11 rounded-full bg-primary-foreground/10 hover:bg-primary-foreground/20 transition-colors">
            <MapPin className="w-5 h-5" />
          </a>
        </div>
      </div>

      <div>
        <h4 className="font-heading text-xl mb-4">Our Services</h4>
        <ul className="space-y-2 text-primary-foreground/80">
          {[["Skilled Nursing", "/services/skilled-nursing"], ["Physical Therapy", "/services/physical-therapy"], ["Occupational Therapy", "/services/occupational-therapy"], ["Speech Therapy", "/services/speech-therapy"], ["Personal Care", "/services/personal-care"], ["Private Duty Care", "/services/private-duty-care"]].map(([l, to]) => (
            <li key={to}>
              <Link to={to} className="hover:text-accent transition-colors">{l}</Link>
            </li>
          ))}
        </ul>
      </div>

      <div>
        <h4 className="font-heading text-xl mb-4">Quick Links</h4>
        <ul className="space-y-2 text-primary-foreground/80">
          {[["Service Areas", "/service-areas"], ["About Us", "/about"], ["Careers", "/careers"], ["Resources", "/resources"], ["Payments", "/payments"], ["Contact", "/contact"]].map(([l, to]) => (
            <li key={to}>
              <Link to={to} className="hover:text-accent transition-colors">{l}</Link>
            </li>
          ))}
        </ul>
      </div>

      <div>
        <h4 className="font-heading text-xl mb-4">Contact</h4>
        <ul className="space-y-3 text-primary-foreground/80">
          <li className="flex gap-3"><MapPin className="w-5 h-5 shrink-0 mt-1" /><span>{COMPANY.address1}<br />{COMPANY.address2}</span></li>
          <li className="flex gap-3"><Phone className="w-5 h-5 shrink-0 mt-1" /><a href={COMPANY.phoneHref} className="hover:text-accent">{COMPANY.phone}</a></li>
          <li className="flex gap-3"><Printer className="w-5 h-5 shrink-0 mt-1" /><span>Fax: {COMPANY.fax}</span></li>
        </ul>
      </div>

      <div>
        <h4 className="font-heading text-xl mb-4 flex items-center gap-2"><Clock className="w-5 h-5" /> Hours</h4>
        <ul className="space-y-1.5 text-sm text-primary-foreground/80">
          {COMPANY.hours.map((h) => (
            <li key={h.day} className="flex justify-between gap-4">
              <span className="font-semibold">{h.day}</span>
              <span>{h.time}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>

    <div className="border-t border-primary-foreground/15">
      <div className="container-page py-6">
        <p className="text-sm text-primary-foreground/60 mb-2 font-semibold uppercase tracking-wide">Areas We Serve</p>
        <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-sm text-primary-foreground/75">
          {AREAS.map((a) => (
            <Link key={a.slug} to={`/service-areas/${a.slug}`} className="hover:text-accent transition-colors">{a.name}, VA</Link>
          ))}
        </div>
      </div>
    </div>

    <div className="border-t border-primary-foreground/15">
      <div className="container-page py-6">
        <p className="text-sm text-primary-foreground/60 mb-2 font-semibold uppercase tracking-wide">Find Us Online</p>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm text-primary-foreground/75">
          {LISTINGS.slice(0, 4).map((l) => (
            <a key={l.name} href={l.url} target="_blank" rel="noreferrer" className="hover:text-accent transition-colors">{l.name}</a>
          ))}
          <Link to="/find-us" className="text-accent font-semibold hover:underline">Where to find us online →</Link>
        </div>
      </div>
    </div>

    <div className="border-t border-primary-foreground/15">
      <div className="container-page py-6 text-sm text-primary-foreground/70 flex flex-col sm:flex-row justify-between items-center gap-3">
        <p>© {new Date().getFullYear()} Esprit Home Healthcare. All Rights Reserved.</p>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 justify-center">
          <Link to="/accessibility" className="hover:text-accent transition-colors">Accessibility</Link>
          <Link to="/privacy" className="hover:text-accent transition-colors">Privacy &amp; Cookie Policy</Link>
          <button onClick={() => window.dispatchEvent(new Event("open-cookie-preferences"))} className="hover:text-accent transition-colors">Cookie Preferences</button>
        </div>
      </div>
    </div>
  </footer>
);
